import { computed, ref } from 'vue'
import { apiClient } from '@/api/client'
import type { components } from '@/api/generated/schema'
import { iconaSicura, paginaClassicaSicura, routeSicura } from '../lib/validazione'
import { HUB, type HubId } from '../lib/hubConfig'

type HubDto = components['schemas']['ConfigurazioneHubDTO']

/** Voce dell'hub, normalizzata (il contratto generato ha tutti i campi opzionali). */
export interface ConfigurazioneVoce {
  idFunzione: number
  /** Titolo IT di ripiego: la traduzione è `menu.<idFunzione>`. */
  titolo: string
  /** Descrizione IT di ripiego: la traduzione è `configurazione.voce.<idFunzione>`. */
  descrizione: string
  /** Classi `pi pi-...` oppure `immi:<chiave>` (stesso formato del menu). */
  icona: string | null
  /** Route del nuovo portale; null se la voce esiste solo nel classico. */
  route: string | null
  classico: boolean
  /** Pagina xhtml del classico (es. `/pages/x/Y.xhtml`). */
  paginaClassica: string | null
  nuovo: boolean
  /** Valorizzato solo per le voci dell'hub Admin (quelle dell'hub Configurazione non lo hanno). */
  hub?: HubId
}

export interface ConfigurazioneArea {
  chiave: string
  /** Titolo IT di ripiego: la traduzione è `configurazione.area.<chiave>`. */
  titolo: string
  descrizione?: string | null
  ordine: number
  voci: ConfigurazioneVoce[]
}

/**
 * Dal DTO del contratto alle aree ordinate, senza aree vuote. Unico punto in cui i valori del server
 * vengono validati: icona (solo `pi pi-…`/`immi:<chiave>` del catalogo), route (solo percorso interno) e
 * pagina del classico (solo `/…xhtml`). Una voce senza destinazione valida viene scartata.
 */
export function normalizzaHub(data: HubDto | undefined, hub: HubId = 'configurazione'): ConfigurazioneArea[] {
  return (data?.aree ?? [])
    .map((a): ConfigurazioneArea => ({
      chiave: a.chiave ?? '',
      titolo: a.titolo ?? a.chiave ?? '',
      descrizione: a.descrizione ?? null,
      ordine: a.ordine ?? 0,
      voci: (a.voci ?? []).flatMap((v): ConfigurazioneVoce[] => {
        const route = routeSicura(v.route)
        const paginaClassica = paginaClassicaSicura(v.paginaClassica)
        // Senza route valida la voce è del classico; il classico dichiarato vale solo con una pagina valida.
        const classico = (v.classico ?? false) || route === null
        if (classico && paginaClassica === null) return []
        return [{
          idFunzione: v.idFunzione ?? 0,
          titolo: v.titolo ?? '',
          descrizione: v.descrizione ?? '',
          icona: iconaSicura(v.icona),
          route: classico ? null : route,
          classico,
          paginaClassica: classico ? paginaClassica : null,
          nuovo: v.nuovo ?? false,
          ...(hub === 'admin' ? { hub } : {}),
        }]
      }),
    }))
    .filter((a) => /^[a-z0-9_-]+$/i.test(a.chiave) && a.voci.length > 0)
    .sort((a, b) => a.ordine - b.ordine)
}

/** Minuscolo e senza accenti: la ricerca non deve dipendere da «è» / «e». */
export function normalizzaTesto(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

/** Testi (già tradotti) con cui una voce viene mostrata e cercata. */
export type TestiVoce = (v: ConfigurazioneVoce) => { titolo: string; descrizione: string }

const testiGrezzi: TestiVoce = (v) => ({ titolo: v.titolo, descrizione: v.descrizione })

/** Filtra le voci di tutte le aree su titolo e descrizione; le aree rimaste vuote spariscono. */
export function filtraAree(aree: ConfigurazioneArea[], ricerca: string, testi: TestiVoce = testiGrezzi): ConfigurazioneArea[] {
  const q = normalizzaTesto(ricerca)
  if (!q) return aree
  return aree
    .map((a) => ({
      ...a,
      voci: a.voci.filter((v) => {
        const t = testi(v)
        return normalizzaTesto(t.titolo).includes(q) || normalizzaTesto(t.descrizione).includes(q)
      }),
    }))
    .filter((a) => a.voci.length > 0)
}

/**
 * Indice delle configurazioni raggruppate per area. Il backend restituisce solo ciò che il
 * profilo può vedere (aree senza voci già omesse): l'accesso è deciso dal contenuto.
 *
 * @param testi come si traducono titolo e descrizione di una voce (serve alla ricerca)
 */
export function useConfigurazioneHub(testi: TestiVoce = testiGrezzi, hub: HubId = 'configurazione') {
  const aree = ref<ConfigurazioneArea[]>([])
  const loading = ref(false)
  const errore = ref(false)
  /** 403: il profilo non ha accesso a questo hub (il backend è autoritativo). */
  const negato = ref(false)
  const ricerca = ref('')

  // Conta solo l'ultima richiesta: «Riprova» ripetuti o un ricarico a metà non si sovrascrivono a vicenda.
  let richiesta = 0
  async function carica(): Promise<void> {
    const mia = ++richiesta
    loading.value = true
    errore.value = false
    negato.value = false
    try {
      const { data } = await apiClient.get<HubDto>(HUB[hub].endpoint)
      if (mia !== richiesta) return
      aree.value = normalizzaHub(data, hub)
    } catch (e) {
      if (mia !== richiesta) return
      negato.value = (e as { response?: { status?: number } })?.response?.status === 403
      errore.value = !negato.value
      aree.value = []
    } finally {
      if (mia === richiesta) loading.value = false
    }
  }

  const areeFiltrate = computed(() => filtraAree(aree.value, ricerca.value, testi))
  const totaleVoci = computed(() => aree.value.reduce((n, a) => n + a.voci.length, 0))

  return { aree, areeFiltrate, totaleVoci, ricerca, loading, errore, negato, carica }
}
